/**
 * Mancala Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → pit selection →
 * seed sowing → capture → extra turns → game over.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + controls + score display.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { GameCoachService, GameContext } from '../../services/GameCoachService';
import { mancalaKnowledge } from '../../services/gameCoachKnowledge';
import { GameCoach } from '../../components/GameCoach';
import { MancalaEngine } from '../MancalaEngine';
import { MancalaAI } from '../MancalaAI';
import { MancalaBoard } from './MancalaBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { MancalaErrorHandler } from '../services/MancalaErrorHandler';
import { MancalaStatisticsService } from '../services/MancalaStatisticsService';
import { MancalaGamePersistence } from '../services/MancalaGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  MancalaGameSettings,
  MancalaGameState,
  PlayerSide,
  MancalaMoveResult,
  MancalaGameProps,
} from '../types';
import './MancalaGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const MancalaGameInner: React.FC<MancalaGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<MancalaGameSettings | null>(null);
  const [gameState, setGameState] = useState<MancalaGameState | null>(null);
  const [animating, setAnimating] = useState(false);
  const [totalCaptures, setTotalCaptures] = useState(0);

  // Refs
  const engineRef = useRef<MancalaEngine>(new MancalaEngine());
  const aiRef = useRef<MancalaAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const humanSideRef = useRef<PlayerSide>('player1');
  const coachRef = useRef(new GameCoachService('Mancala', mancalaKnowledge));

  // Services (lazy init)
  const errorHandlerRef = useRef<MancalaErrorHandler | null>(null);
  const statsServiceRef = useRef<MancalaStatisticsService | null>(null);
  const persistenceRef = useRef<MancalaGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new MancalaErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new MancalaStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new MancalaGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // Check for saved game on mount
  useEffect(() => {
    if (persistenceRef.current?.hasAutoSave()) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Saved Game Found',
        message: 'A saved game was found. Start a new game to continue.',
        duration: 4000,
      });
    }
  }, [addNotification]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: MancalaGameSettings) => {
      const engine = engineRef.current;
      engine.initializeGame(newSettings);

      // Determine human side
      const humanSide = newSettings.playerSide || 'player1';
      humanSideRef.current = humanSide;

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setAnimating(false);
      setTotalCaptures(0);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('mancala');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new MancalaAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${newSettings.mode === 'singleplayer' ? 'Single player' : 'Multiplayer'} game started!`,
        duration: 2000,
      });

      // If AI goes first (human chose player2), trigger AI move
      if (
        newSettings.mode === 'singleplayer' &&
        humanSide === 'player2' &&
        aiRef.current
      ) {
        const state = engine.getGameState();
        aiRef.current.scheduleMove(state.board, state.currentPlayer, (pitIndex) => {
          if (pitIndex >= 0) handleAIMove(pitIndex);
        });
      }
    },
    [addNotification]
  );

  const handlePitClick = useCallback(
    (pitIndex: number) => {
      const engine = engineRef.current;
      if (engine.isGameOver() || animating) return;

      const currentPlayer = engine.getCurrentPlayer();

      // In single player, only allow human to click
      if (settings?.mode === 'singleplayer') {
        if (currentPlayer !== humanSideRef.current) return;
      }

      executeMove(currentPlayer, pitIndex);
    },
    [animating, settings]
  );

  const executeMove = useCallback(
    (player: PlayerSide, pitIndex: number) => {
      const engine = engineRef.current;

      try {
        const result: MancalaMoveResult = engine.executeMove(player, pitIndex);
        setAnimating(true);

        // Track captures
        if (result.captured) {
          setTotalCaptures(prev => prev + 1);
          addNotification({
            type: NotificationType.INFO,
            title: `🎯 Capture!`,
            message: `${player === 'player1' ? 'Player 1' : 'Player 2'} captured ${result.capturedSeeds} seeds!`,
            duration: 2000,
          });
        }

        // Show extra turn notification
        if (result.extraTurn) {
          addNotification({
            type: NotificationType.INFO,
            title: '🔄 Extra Turn!',
            message: `${player === 'player1' ? 'Player 1' : 'Player 2'} gets another turn!`,
            duration: 1500,
          });
        }

        // After animation, advance turn
        setTimeout(() => {
          setAnimating(false);

          // Advance turn
          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);

          // Check if game is over
          if (engine.isGameOver()) {
            handleGameOver(newState);
            return;
          }

          // Auto-save
          if (persistenceRef.current && settings) {
            persistenceRef.current.autoSave(settings.mode, newState, {
              aiDifficulty: settings.aiDifficulty,
              startedAt: newState.startedAt,
              lastSavedAt: Date.now(),
            });
          }

          // If next player is AI, schedule move
          if (
            settings?.mode === 'singleplayer' &&
            aiRef.current &&
            newState.currentPlayer !== humanSideRef.current &&
            !engine.isGameOver()
          ) {
            aiRef.current.scheduleMove(
              newState.board,
              newState.currentPlayer,
              (nextPitIndex) => {
                if (nextPitIndex >= 0) handleAIMove(nextPitIndex);
              }
            );
          }
        }, 400);
      } catch (error) {
        setAnimating(false);
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification, settings]
  );

  const handleAIMove = useCallback(
    (pitIndex: number) => {
      const engine = engineRef.current;
      if (engine.isGameOver()) return;

      const currentPlayer = engine.getCurrentPlayer();
      executeMove(currentPlayer, pitIndex);
    },
    [executeMove]
  );

  const handleGameOver = useCallback(
    (state: MancalaGameState) => {
      setGameState(state);
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settings) {
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: settings.mode,
            difficulty: settings.aiDifficulty,
            winner: state.winner,
            score: state.score,
            totalMoves: state.moveHistory.length,
            totalCaptures,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          humanSideRef.current,
          isAuthenticated ? user?.id : undefined
        );
      }

      // Show result notification
      const winner = state.winner;
      if (winner === 'draw') {
        addNotification({
          type: NotificationType.INFO,
          title: '🤝 Draw!',
          message: `Game ended in a draw! ${state.score.player1} - ${state.score.player2}`,
          duration: 5000,
        });
      } else {
        const winnerLabel = winner === 'player1' ? 'Player 1' : 'Player 2';
        addNotification({
          type: NotificationType.SUCCESS,
          title: '🎉 Game Over!',
          message: `${winnerLabel} wins ${state.score.player1} - ${state.score.player2}!`,
          duration: 5000,
        });
      }
    },
    [addNotification, settings, isAuthenticated, user, totalCaptures]
  );

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setSettings(null);
    setTotalCaptures(0);
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Trigger AI on initial turn if AI goes first
  // =========================================================================
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    const engine = engineRef.current;
    const currentPlayer = engine.getCurrentPlayer();
    if (
      currentPlayer !== humanSideRef.current &&
      !engine.isGameOver() &&
      !animating
    ) {
      aiRef.current.scheduleMove(gameState.board, currentPlayer, (pitIndex) => {
        if (pitIndex >= 0) handleAIMove(pitIndex);
      });
    }
    // Only run when view changes to playing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const mancalaConfig: GameModeConfig = {
      gameName: 'Mancala',
      gameIcon: '🫘',
      availableModes: ['ai', 'local', 'online'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '☀️', description: 'AI makes mistakes' },
        { id: 'medium', label: 'Medium', icon: '⭐', description: 'Balanced gameplay' },
        { id: 'hard', label: 'Hard', icon: '🏆', description: 'Expert-level AI' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai' && selection.difficulty) {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: selection.difficulty as MancalaGameSettings['aiDifficulty'],
        });
      } else if (selection.mode === 'local') {
        handleStartGame({ mode: 'local-multiplayer' });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="mancala-game">
        <UniversalGameModeSelector
          config={mancalaConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const currentPlayer = gameState.currentPlayer;
  const isHumanTurn =
    settings?.mode !== 'singleplayer' || currentPlayer === humanSideRef.current;
  const validPits = isHumanTurn && !animating && gameState.gameStatus === 'active'
    ? engineRef.current.getValidMoves(currentPlayer)
    : [];

  // Game over config
  const gameOverConfig = (view === 'game-over' && gameState.gameStatus === 'completed') ? {
    show: true,
    result: (gameState.winner === 'draw' ? 'draw' : 'win') as 'win' | 'loss' | 'draw',
    title: gameState.winner === 'draw' ? '🤝 Draw!' : '🎉 Game Over!',
    subtitle: gameState.winner === 'draw'
      ? `Game ended in a draw! ${gameState.score.player1} - ${gameState.score.player2}`
      : `${gameState.winner === 'player1' ? 'Player 1' : 'Player 2'} wins ${gameState.score.player1} - ${gameState.score.player2}!`,
    icon: gameState.winner === 'draw' ? '🤝' : '🎉',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Mancala',
    board: (
      <div className="mancala-game__board-area">
        <MancalaBoard
          board={gameState.board}
          currentPlayer={currentPlayer}
          validPits={validPits}
          lastMoveResult={gameState.lastMoveResult}
          onPitClick={handlePitClick}
          disabled={!isHumanTurn || animating || gameState.gameStatus === 'completed'}
          animating={animating}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'scores',
        title: 'Scores',
        icon: '📊',
        content: (
          <div className="mancala-game__score-bar" role="status" aria-live="polite">
            <div
              className={`mancala-game__score mancala-game__score--p2 ${currentPlayer === 'player2' ? 'mancala-game__score--active' : ''}`}
            >
              <span className="mancala-game__score-icon" aria-hidden="true">⬆️</span>
              <span className="mancala-game__score-label">Player 2</span>
              <span className="mancala-game__score-value">{gameState.score.player2}</span>
            </div>
            <div
              className={`mancala-game__score mancala-game__score--p1 ${currentPlayer === 'player1' ? 'mancala-game__score--active' : ''}`}
            >
              <span className="mancala-game__score-icon" aria-hidden="true">⬇️</span>
              <span className="mancala-game__score-label">Player 1</span>
              <span className="mancala-game__score-value">{gameState.score.player1}</span>
            </div>
          </div>
        ),
      },
      {
        id: 'info',
        title: 'Game Info',
        icon: '📋',
        content: (
          <div className="mancala-game__info">
            <span>Moves: {gameState.moveHistory.length}</span>
            {totalCaptures > 0 && <span>Captures: {totalCaptures}</span>}
            {validPits.length > 0 && isHumanTurn && (
              <span>{validPits.length} valid pit{validPits.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        ),
      },
    ],
    rightPanels: settings?.mode === 'singleplayer' ? [
      {
        id: 'coach',
        title: 'AI Coach',
        icon: '🧠',
        content: (
          <GameCoach
            coach={coachRef.current}
            gameContext={gameState ? {
              gameName: 'Mancala',
              gameState: `Score: Player 1: ${gameState.score.player1}, Player 2: ${gameState.score.player2}. Move ${gameState.moveHistory.length}. ${validPits.length} valid pits.`,
              playerContext: `Playing as ${humanSideRef.current === 'player1' ? 'Player 1' : 'Player 2'}. ${isHumanTurn ? "It's your turn." : "Waiting for AI."}`,
            } : null}
            isPlayerTurn={isHumanTurn}
            quickQuestions={['Extra turn?', 'Capture setup?', 'Count stones?', 'Opening move?']}
            accentColor="#92400e"
          />
        ),
      },
    ] : undefined,
    statusBar: {
      turnIndicator: (
        <span>
          {gameState.gameStatus === 'completed'
            ? 'Game Over'
            : !isHumanTurn
              ? 'AI thinking...'
              : `${currentPlayer === 'player1' ? '⬇️' : '⬆️'} ${currentPlayer === 'player1' ? 'Player 1' : 'Player 2'}'s turn`}
        </span>
      ),
      gameStatus: gameState.gameStatus === 'completed' ? 'Game Over' : undefined,
      gameStatusType: gameState.gameStatus === 'completed' ? 'ended' : 'active',
      aiThinking: !isHumanTurn && gameState.gameStatus === 'active',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="mancala-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const MancalaGame: React.FC<MancalaGameProps> = (props) => (
  <ErrorBoundary componentName="Mancala">
    <MancalaGameInner {...props} />
  </ErrorBoundary>
);

export { MancalaGame };
export default MancalaGame;
